import type { CoachAdviceItem } from '@strategy4kids/ai-coach';
import type { Grade } from '@strategy4kids/lessons';
import type { BackgammonTurnRecord, GameSnapshot, LiveClock } from '@strategy4kids/protocol';

const BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export interface MintResult {
  readonly playerId: string;
  readonly name: string;
  readonly avatar: string | null;
  readonly token: string;
}

export interface PlayerInfo {
  readonly id: string;
  readonly name: string;
  readonly avatar: string | null;
}

export interface PlayerRating {
  readonly rating: number;
  readonly deviation: number;
  readonly provisional: boolean;
}

export interface ProfilePayload {
  readonly player: PlayerInfo;
  readonly rating: PlayerRating;
}

export interface GameSummary {
  readonly id: string;
  readonly status: string;
  readonly mode: string;
  readonly opponent: 'ai' | 'human';
  readonly rated: boolean;
  readonly result: unknown;
  readonly winner: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CreateGameInput {
  readonly opponent: 'ai' | 'human';
  readonly side?: 'w' | 'b' | 'random';
  readonly mode: 'casual' | 'rated';
  readonly elo?: number;
  /** Online backgammon (2026-10-04): casual human-vs-human only, server-side
   *  enforced. Absent = chess. */
  readonly variant?: 'chess' | 'checkers' | 'backgammon';
}

export interface LeaderboardRow {
  readonly nickname: string;
  readonly rating: number;
}

export interface ParentSession {
  readonly parentId: string;
  readonly email: string;
  readonly token: string;
}

/** Parent appearance prefs — mirrored server-side for cross-device sync (P3). */
export interface ParentPrefs {
  readonly theme?: 'light' | 'dark' | 'system';
  readonly accent?: 'default' | 'blossom' | 'ocean';
}

export interface KidPrefs {
  readonly coachLevel: 'gentle' | 'balanced' | 'advanced';
  readonly screenTimeMinutes: number;
}

export interface KidProfile extends KidPrefs {
  readonly id: string;
  readonly playerId: string;
  readonly nickname: string;
  readonly playerName: string;
  readonly rating: number | null;
}

export interface AddKidInput {
  readonly nickname: string;
  readonly playerToken: string;
}

async function apiFetch<T>(path: string, init: RequestInit = {}, base: string = BASE): Promise<T> {
  let res: Response;
  const headers = init.body
    ? { 'Content-Type': 'application/json', ...(init.headers ?? {}) }
    : init.headers;
  try {
    res = await fetch(`${base}${path}`, { ...init, headers });
  } catch {
    throw new ApiError(0, 'Cannot reach the server.');
  }
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: unknown };
      if (typeof body.error === 'string') message = body.error;
    } catch {
      // non-JSON error body — keep the default message
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as T;
}

const bearer = (token: string) => ({ authorization: `Bearer ${token}` });

/**
 * `?engine=fake` is parsed by `parseQuery()` in `lib/query.ts` for the chess AI
 * to swap createServerEngine → createFakeEngine. The audit (`audit:design-quality`)
 * reuses the same flag to seed leaderboard + parent-dashboard data when the real
 * API isn't reachable. The seed data is short, deterministic, and ships no PII.
 */
function isFakeApi(): boolean {
  if (typeof window === 'undefined') return false;
  return new URL(window.location.href).searchParams.get('engine') === 'fake';
}

const FAKE_LEADERBOARD: readonly LeaderboardRow[] = [
  { nickname: 'Ruby', rating: 1820 },
  { nickname: 'Leo', rating: 1640 },
  { nickname: 'Mia', rating: 1570 },
  { nickname: 'Eli', rating: 1490 },
  { nickname: 'Nora', rating: 1430 },
];

const FAKE_KIDS: readonly KidProfile[] = [
  {
    id: 'fake-k1',
    playerId: 'fake-p1',
    nickname: 'Ruby',
    playerName: 'Ruby',
    coachLevel: 'gentle',
    screenTimeMinutes: 30,
    rating: 1320,
  },
  {
    id: 'fake-k2',
    playerId: 'fake-p2',
    nickname: 'Leo',
    playerName: 'Leo',
    coachLevel: 'balanced',
    screenTimeMinutes: 60,
    rating: 1500,
  },
  {
    id: 'fake-k3',
    playerId: 'fake-p3',
    nickname: 'Mia',
    playerName: 'Mia',
    coachLevel: 'advanced',
    screenTimeMinutes: 90,
    rating: 1640,
  },
];

const FAKE_PARENT_SESSION: ParentSession = {
  parentId: 'fake-parent',
  email: 'audit+fake@strategy4kids.local',
  token: 'fake-parent-token',
};

/**
 * Seed name used by the `?engine=fake` mint short-circuit. The online-game
 * audit flow fills the profile input with this exact value so the returned
 * `name` field is deterministic across runs (the fake `fetchProfile` echoes
 * it back into the rendered rating card).
 */
const FAKE_PROFILE_NAME = 'Auditor';

/**
 * Room code returned by the `?engine=fake` createGame short-circuit.
 *
 * Deliberately a constant rather than a random `FAKE-XXXX`. Two things in
 * this repo cannot cope with a random one:
 *
 *  - The `online-game` audit flow wants to assert the code that reaches the
 *    UI, and `toHaveText` can only compare a whole string. A RegExp is in the
 *    type (`types/expectation.ts`) but cannot survive flow loading: the runner
 *    loads every `.flow.ts` in a tsx subprocess and ships it back over
 *    `JSON.stringify`/`JSON.parse` (screen-audit-runner `discover.ts:43-49`),
 *    which turns a RegExp into `{}` and dies on `exp.text.test is not a
 *    function`.
 *  - `room-code` renders at `text-3xl` and lands in that step's screenshot, so
 *    a random code would give the visual-regression golden for the step
 *    different pixels on every run.
 *
 * Nothing keys off the id in fake mode — OnlineLobby only reads `game.id` to
 * display it, and there is no server to collide with.
 */
const FAKE_ROOM_CODE = 'FAKE-C0DE';

export const api = {
  /**
   * The operator's per-game stages for this deployment (docs/proposals/remote-game-stages.md). The body is returned as `unknown` on purpose:
   * the caller validates it, and a malformed one is ignored. `null` under `?engine=fake` (audits and most e2e): no network, deterministic.
   */
  getGameStages(): Promise<unknown | null> {
    if (isFakeApi()) return Promise.resolve(null);
    return apiFetch<unknown>('/api/game-stages');
  },

  // Players (mint is public; profile reads send Bearer, not a URL token).
  createProfile(name: string): Promise<MintResult> {
    if (isFakeApi()) {
      // The online-game audit flow fills the profile-name input with
      // FAKE_PROFILE_NAME so the returned `name` is deterministic; for any
      // other input we still pass through so the user sees the name they
      // typed in the rating card.
      const safeName = name.trim() === '' ? FAKE_PROFILE_NAME : name.trim();
      return Promise.resolve<MintResult>({
        playerId: `fake-player-${safeName}`,
        name: safeName,
        avatar: null,
        token: `fake-token-${safeName}`,
      });
    }
    return apiFetch<MintResult>('/api/players', { method: 'POST', body: JSON.stringify({ name }) });
  },
  fetchProfile(token: string): Promise<ProfilePayload> {
    if (isFakeApi()) {
      return Promise.resolve<ProfilePayload>({
        player: { id: `fake-player-${token}`, name: FAKE_PROFILE_NAME, avatar: null },
        rating: { rating: 1500, deviation: 350, provisional: true },
      });
    }
    return apiFetch<ProfilePayload>('/api/players/me', { headers: bearer(token) });
  },
  listGames(token: string): Promise<GameSummary[]> {
    if (isFakeApi()) return Promise.resolve<GameSummary[]>([]);
    return apiFetch<{ games: GameSummary[] }>('/api/players/me/games', {
      headers: bearer(token),
    }).then((d) => d.games);
  },

  // Games (Bearer).
  createGame(token: string, input: CreateGameInput): Promise<GameSnapshot> {
    if (isFakeApi()) {
      // OnlineLobby only reads `game.id` from the create-game response
      // (line 111: `setFriend({ gameId: game.id })`). The shape here
      // matches the server contract well enough for the rest of the
      // lobby to mount; we cast to GameSnapshot to keep the typed
      // surface intact.
      const id = FAKE_ROOM_CODE;
      const now = new Date().toISOString();
      const fake = {
        id,
        status: 'pending' as const,
        mode: input.mode,
        opponent: input.opponent,
        rated: input.mode === 'rated',
        result: null,
        winner: null,
        createdAt: now,
        updatedAt: now,
      };
      return Promise.resolve(fake as unknown as GameSnapshot);
    }
    return apiFetch<{ game: GameSnapshot }>('/api/games', {
      method: 'POST',
      headers: bearer(token),
      body: JSON.stringify(input),
    }).then((d) => d.game);
  },
  fetchGame(token: string, gameId: string): Promise<GameSnapshot> {
    return apiFetch<{ game: GameSnapshot }>(`/api/games/${encodeURIComponent(gameId)}`, {
      headers: bearer(token),
    }).then((d) => d.game);
  },
  /** Server roll for online backgammon — the client never supplies dice. */
  rollBackgammon(token: string, gameId: string): Promise<GameSnapshot> {
    return apiFetch<{ game: GameSnapshot }>(`/api/games/${encodeURIComponent(gameId)}/roll`, {
      method: 'POST',
      headers: bearer(token),
    }).then((d) => d.game);
  },
  /** Submit a complete backgammon turn; the server replays it against the
   *  engine's legal turns from the stored snapshot. */
  submitBackgammonTurn(
    token: string,
    gameId: string,
    record: BackgammonTurnRecord,
  ): Promise<GameSnapshot> {
    return apiFetch<{ game: GameSnapshot }>(`/api/games/${encodeURIComponent(gameId)}/turn`, {
      method: 'POST',
      headers: bearer(token),
      body: JSON.stringify(record),
    }).then((d) => d.game);
  },
  /** Read-only view of a game (spectators): the server falls back to the
   *  spectating snapshot when the caller is not a participant. */
  spectateGame(token: string, gameId: string): Promise<GameSnapshot> {
    return apiFetch<{ game: GameSnapshot }>(`/api/games/${encodeURIComponent(gameId)}/live`, {
      headers: bearer(token),
    }).then((d) => d.game);
  },
  /** One doubling-cube action: 'double' offers instead of rolling; 'take' /
   *  'pass' answer the opponent's offer. Role is inferred server-side. */
  cubeBackgammon(
    token: string,
    gameId: string,
    action: 'double' | 'take' | 'pass',
  ): Promise<GameSnapshot> {
    return apiFetch<{ game: GameSnapshot }>(`/api/games/${encodeURIComponent(gameId)}/cube`, {
      method: 'POST',
      headers: bearer(token),
      body: JSON.stringify({ action }),
    }).then((d) => d.game);
  },
  joinGame(token: string, gameId: string): Promise<GameSnapshot> {
    return apiFetch<{ game: GameSnapshot }>(`/api/games/${encodeURIComponent(gameId)}/join`, {
      method: 'POST',
      headers: bearer(token),
    }).then((d) => d.game);
  },
  submitMove(token: string, gameId: string, uci: string): Promise<GameSnapshot> {
    return apiFetch<{ game: GameSnapshot }>(`/api/games/${encodeURIComponent(gameId)}/moves`, {
      method: 'POST',
      headers: bearer(token),
      body: JSON.stringify({ move: uci }),
    }).then((d) => d.game);
  },
  resignGame(token: string, gameId: string): Promise<GameSnapshot> {
    return apiFetch<{ game: GameSnapshot }>(`/api/games/${encodeURIComponent(gameId)}/resign`, {
      method: 'POST',
      headers: bearer(token),
    }).then((d) => d.game);
  },

  // Coach (Bearer).
  fetchCoachAdvice(
    token: string,
    gameId: string,
  ): Promise<{ moves: CoachAdviceItem[]; assistanceLevel: number }> {
    return apiFetch('/api/coach/advice', {
      method: 'POST',
      headers: bearer(token),
      body: JSON.stringify({ gameId }),
    });
  },

  // Lesson progress (Bearer).
  fetchLessonProgress(token: string): Promise<Record<string, Grade>> {
    if (isFakeApi()) return Promise.resolve<Record<string, Grade>>({});
    return apiFetch<{ lessons: Array<{ id: string; bestGrade: Grade | null }> }>('/api/lessons', {
      headers: bearer(token),
    }).then((d) => {
      const out: Record<string, Grade> = {};
      for (const lesson of d.lessons) if (lesson.bestGrade) out[lesson.id] = lesson.bestGrade;
      return out;
    });
  },
  completeLesson(
    token: string,
    lessonId: string,
    grade: Grade,
  ): Promise<{ completed: boolean; bestGrade: Grade }> {
    return apiFetch(`/api/lessons/${encodeURIComponent(lessonId)}/complete`, {
      method: 'POST',
      headers: bearer(token),
      body: JSON.stringify({ grade }),
    });
  },

  // Matchmaking (Bearer).
  joinMatchmaking(
    token: string,
    input: { elo: number; range?: number; color?: 'w' | 'b'; variant?: 'chess' | 'backgammon' },
  ): Promise<{ queueId: string; gameId?: string }> {
    if (isFakeApi()) {
      // No `gameId` → the lobby enters the queued state instead of
      // immediately routing to the play screen. Matches the real server's
      // "no match found yet, wait for the SSE event" shape.
      return Promise.resolve<{ queueId: string; gameId?: string }>({
        queueId: 'fake-q',
        gameId: undefined,
      });
    }
    return apiFetch('/api/matchmaking/join', {
      method: 'POST',
      headers: bearer(token),
      body: JSON.stringify(input),
    });
  },
  leaveMatchmaking(token: string): Promise<{ ok: boolean }> {
    if (isFakeApi()) return Promise.resolve({ ok: true });
    return apiFetch('/api/matchmaking/leave', { method: 'DELETE', headers: bearer(token) });
  },

  // Live play (Bearer).
  fetchLiveGame(token: string, gameId: string): Promise<{ game: GameSnapshot; clock: LiveClock }> {
    return apiFetch(`/api/games/${encodeURIComponent(gameId)}/live`, { headers: bearer(token) });
  },

  // Voice (Bearer).
  tts(token: string, text: string, voice?: string): Promise<{ url: string; fromCache: boolean }> {
    return apiFetch('/api/coach/tts', {
      method: 'POST',
      headers: bearer(token),
      body: JSON.stringify({ text, voice }),
    });
  },

  // Leaderboard (public).
  fetchLeaderboard(limit = 20): Promise<LeaderboardRow[]> {
    if (isFakeApi()) return Promise.resolve(FAKE_LEADERBOARD.slice(0, limit));
    return apiFetch<{ rows: LeaderboardRow[] }>(`/api/leaderboard?limit=${limit}`).then(
      (d) => d.rows,
    );
  },

  // Parent accounts (signup/login public; the rest send the parent session Bearer).
  parentSignup(email: string, password: string): Promise<ParentSession> {
    if (isFakeApi()) return Promise.resolve({ ...FAKE_PARENT_SESSION, email });
    return apiFetch<ParentSession>('/api/parent/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  parentLogin(email: string, password: string): Promise<ParentSession> {
    if (isFakeApi()) return Promise.resolve({ ...FAKE_PARENT_SESSION, email });
    return apiFetch<ParentSession>('/api/parent/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  /** Is a support inbox wired up for this deployment? Hides the button while it is not. */
  supportStatus(token: string): Promise<{ configured: boolean }> {
    // Fake mode (audits, e2e) never offers support: no extra UI in the baselines, no request.
    if (isFakeApi()) return Promise.resolve({ configured: false });
    return apiFetch('/api/support/status', { headers: bearer(token) });
  },
  /** Send a support ticket as the signed-in parent. Identity comes from the token, never the body. */
  createSupportTicket(
    token: string,
    input: { subject: string; description: string; context?: { route?: string; url?: string } },
  ): Promise<{ id: string }> {
    if (isFakeApi()) return Promise.reject(new ApiError(503, 'support_not_configured'));
    return apiFetch<{ data: { id: string } }>('/api/support/tickets', {
      method: 'POST',
      headers: bearer(token),
      body: JSON.stringify(input),
    }).then((r) => r.data);
  },
  fetchParentMe(token: string): Promise<{ id: string; email: string }> {
    return apiFetch('/api/parent/me', { headers: bearer(token) });
  },
  getParentPrefs(token: string): Promise<ParentPrefs> {
    if (isFakeApi()) return Promise.resolve({});
    return apiFetch<{ prefs: ParentPrefs }>('/api/parent/prefs', { headers: bearer(token) }).then(
      (d) => d.prefs,
    );
  },
  updateParentPrefs(token: string, prefs: ParentPrefs): Promise<ParentPrefs> {
    if (isFakeApi()) return Promise.resolve(prefs);
    return apiFetch<{ prefs: ParentPrefs }>('/api/parent/prefs', {
      method: 'PUT',
      headers: bearer(token),
      body: JSON.stringify(prefs),
    }).then((d) => d.prefs);
  },
  fetchParentKids(token: string): Promise<KidProfile[]> {
    if (isFakeApi()) return Promise.resolve(FAKE_KIDS.map((k) => ({ ...k })));
    return apiFetch<{ kids: KidProfile[] }>('/api/parent/kids', { headers: bearer(token) }).then(
      (d) => d.kids,
    );
  },
  addKid(token: string, input: AddKidInput): Promise<KidProfile> {
    return apiFetch<{ kid: KidProfile }>('/api/parent/kids', {
      method: 'POST',
      headers: bearer(token),
      body: JSON.stringify(input),
    }).then((d) => d.kid);
  },
  updateKidPrefs(
    token: string,
    kidId: string,
    prefs: Partial<KidPrefs> & { resetRatings?: boolean },
  ): Promise<KidProfile> {
    return apiFetch<{ kid: KidProfile }>(`/api/kids/${encodeURIComponent(kidId)}/prefs`, {
      method: 'PATCH',
      headers: bearer(token),
      body: JSON.stringify(prefs),
    }).then((d) => d.kid);
  },
  parentLogout(token: string): Promise<{ ok: boolean }> {
    return apiFetch('/api/parent/session', { method: 'DELETE', headers: bearer(token) });
  },
};

/**
 * The card pack marketplace (apps/marketplace): a separate service with its own origin, set at build time by
 * `VITE_MARKETPLACE_URL`. Empty means the feature is off, which is how it stays dormant until the service is deployed.
 * Same rule as everything else here: the only `fetch` is in this file.
 */
export const MARKETPLACE_URL: string = String(import.meta.env.VITE_MARKETPLACE_URL ?? '').replace(/\/+$/, '');

export interface MarketplacePackInfo {
  readonly id: string;
  readonly name: string;
  readonly tagline: string;
  readonly version: string;
  readonly author: string;
  readonly licence: string;
  readonly contentRating: 'everyone' | 'mature';
  readonly official: boolean;
  readonly tags: readonly string[];
  /** The pack's images, each with its sha256 and path on the marketplace. */
  readonly files: Readonly<Record<string, { readonly sha256: string; readonly url: string }>>;
}

const edition_q = (edition: 'kids' | 'adult') => `?edition=${edition}`;

export const marketplace = {
  /** A share link's pack, without using up a redemption. Rejects with a 404 for an expired, revoked or unknown link and 403 if this edition may not have it. */
  getShare(token: string, edition: 'kids' | 'adult'): Promise<MarketplacePackInfo> {
    return marketplaceFetch(`/api/share/${encodeURIComponent(token)}${edition_q(edition)}`);
  },
  /** Redeems a share link: the same pack, and one use of the link counted. */
  redeemShare(token: string, edition: 'kids' | 'adult'): Promise<MarketplacePackInfo> {
    return marketplaceFetch(`/api/share/${encodeURIComponent(token)}/redeem${edition_q(edition)}`, { method: 'POST' });
  },
  /** The packs listed for this edition (the kids edition gets only official, everyone-rated ones). No image list: ask for one pack for that. */
  listPacks(edition: 'kids' | 'adult'): Promise<{ readonly packs: readonly Omit<MarketplacePackInfo, 'files'>[] }> {
    return marketplaceFetch(`/api/packs${edition_q(edition)}`);
  },
  /** One listed pack with its image list, the same rule applied for the edition. */
  getPack(id: string, edition: 'kids' | 'adult'): Promise<MarketplacePackInfo> {
    return marketplaceFetch(`/api/packs/${encodeURIComponent(id)}${edition_q(edition)}`);
  },
};

function marketplaceFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (MARKETPLACE_URL === '') return Promise.reject(new ApiError(0, 'The pack marketplace is not available.'));
  return apiFetch<T>(path, init, MARKETPLACE_URL);
}
